import axios from 'axios';
import { autoMarkingUploadURL, autoMarkingHistoryURL, autoMarkingResultURL, autoMarkingExportURL, autoMarkingBaseURL } from '../constants/ApiEndPoints';
import { getAccessToken } from './AxiosServices';

// Types for Auto Marking
export interface QuestionDetail {
  questionNumber: string;
  questionText: string;
  taskType: string;
  studentAnswer: string;
  correctAnswer: string;
  status: 'correct' | 'partial' | 'incorrect';
  marksAwarded: number;
  maxMarks: number;
  feedback?: string;
}

export interface MarkingSummary {
  totalQuestions: number;
  correctAnswers: number;
  partiallyCorrect: number;
  incorrect: number;
  unanswered: number;
  totalMarks: number;
  maxMarks: number;
  percentage: number;
}

export interface AutoMarkingData {
  subject_name: string;
  module?: string;
  moduleCode?: string;
  pageNumber?: string;
  handbookPage?: string;
  summary: MarkingSummary;
  questionDetails: QuestionDetail[];
  teacherSummary?: string;
  questionsAndAnswers?: any;
  handbookAnswer?: any;
}

export interface AutoMarkingResponse {
  fileName: string;
  fileSize: number;
  uploadTime: string;
  success?: boolean;
  error?: string;
  data?: AutoMarkingData;
  summary?: MarkingSummary;
  questionDetails?: QuestionDetail[];
  imageUrl?: string; // Add image URL for preview
  imageFile?: File; // Add original file for preview
}

export interface ApiResponse {
  success: boolean;
  message?: string;
  data?: AutoMarkingData;
}

// Utility functions
export const validateFile = (file: File): { valid: boolean; error?: string } => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/bmp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File size exceeds 10MB limit'
    };
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'File type not supported. Please upload PNG, JPG, GIF, BMP, PDF, DOC, or DOCX files.'
    };
  }

  return { valid: true };
};

// Test function to check API connectivity
// Auto Marking Service Functions
export const uploadForMarking = async (file: File): Promise<ApiResponse> => {
  try {
    // Validate file first
    const validation = validateFile(file);
    if (!validation.valid) {
      return {
        success: false,
        message: validation.error || 'File validation failed'
      };
    }

    console.log('Starting file upload:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      endpoint: autoMarkingUploadURL
    });

    // Try different approaches to see which one works
    
    // Approach 1: FormData with 'image' field (current)
    const formData = new FormData();
    formData.append('image', file);

    console.log('FormData created:', {
      hasImage: formData.has('image'),
      entries: Array.from(formData.entries()).map(([key, value]) => ({
        key,
        valueType: typeof value,
        isFile: value instanceof File,
        fileName: value instanceof File ? value.name : 'not a file'
      }))
    });

    try {
      console.log('Attempting FormData upload...');
      
      // Get authorization token
      const token = getAccessToken();
      
      // Try with fetch API instead of axios to see if it handles CORS better
      const response = await fetch(autoMarkingUploadURL, {
        method: 'POST',
        body: formData,
        mode: 'cors', // Explicitly set CORS mode
        headers: {
          'Authorization': token || ''
        }
      });

      console.log('Fetch response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Fetch upload successful:', data);

      return await handleApiResponse({ data, status: response.status, statusText: response.statusText });
      
    } catch (fetchError: any) {
      console.log('Fetch upload failed, trying axios...');
      console.error('Fetch error:', fetchError.message);
      
      // Fallback to axios
      try {
        const axiosResponse = await axios.post(autoMarkingUploadURL, formData, {
          timeout: 120000,
          // Remove CORS headers as they don't work from client side
          withCredentials: false,
        });

        console.log('Axios upload successful:', axiosResponse.data);
        return await handleApiResponse(axiosResponse);
        
      } catch (axiosError: any) {
        console.log('Axios also failed, trying with "file" field name...');
        console.error('Axios error:', axiosError.response?.data || axiosError.message);
        
        // Approach 2: Try with 'file' field name instead of 'image'
        try {
          console.log('Attempting upload with "file" field name...');
          const formData2 = new FormData();
          formData2.append('file', file);
          
          const response2 = await axios.post(autoMarkingUploadURL, formData2, {
            timeout: 120000,
            withCredentials: false,
          });
          
          console.log('File field upload successful:', response2.data);
          return await handleApiResponse(response2);
        } catch (fileFieldError: any) {
          console.log('File field upload also failed...');
          console.error('File field error:', fileFieldError.response?.data || fileFieldError.message);
          
          // Return the original error
          throw axiosError;
        }
      }
    }

  } catch (error: any) {
    console.error('Auto marking upload error:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      statusText: error.response?.statusText,
      headers: error.response?.headers,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      }
    });
    
    if (error.response) {
      // Server responded with error status
      const errorMessage = error.response.data?.message || 
                          error.response.data?.error || 
                          JSON.stringify(error.response.data) ||
                          `Server error: ${error.response.status} - ${error.response.statusText}`;
      
      return {
        success: false,
        message: errorMessage
      };
    } else if (error.message?.includes('CORS')) {
      return {
        success: false,
        message: 'CORS error: The API server needs to allow requests from this domain. Please contact the API administrator to add CORS headers.'
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        success: false,
        message: 'No response from server. Please check your connection.'
      };
    } else {
      // Something else happened
      return {
        success: false,
        message: error.message || 'An unexpected error occurred'
      };
    }
  }
};

// Alternative upload function that might work better with CORS
export const uploadForMarkingDirect = async (file: File): Promise<ApiResponse> => {
  try {
    console.log('Attempting direct upload without CORS restrictions...');
    
    // Create a form and submit it directly (this bypasses CORS in some cases)
    const formData = new FormData();
    formData.append('image', file);
    
    // Get authorization token
    const token = getAccessToken();
    
    // Use XMLHttpRequest instead of fetch/axios
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          console.log('XHR Response:', {
            status: xhr.status,
            statusText: xhr.statusText,
            responseText: xhr.responseText
          });
          
          if (xhr.status === 200) {
            try {
              const data = JSON.parse(xhr.responseText);
              console.log('Parsed XHR data:', data);
              
              // Pass through handleApiResponse to properly extract data from collections
              handleApiResponse({ data, status: xhr.status, statusText: xhr.statusText })
                .then(result => resolve(result))
                .catch(err => resolve({
                  success: false,
                  message: err.message || 'Failed to process response'
                }));
            } catch (e) {
              resolve({
                success: false,
                message: 'Invalid JSON response'
              });
            }
          } else {
            resolve({
              success: false,
              message: `HTTP ${xhr.status}: ${xhr.statusText}`
            });
          }
        }
      };
      
      xhr.onerror = function() {
        console.error('XHR Error');
        resolve({
          success: false,
          message: 'Network error occurred'
        });
      };
      
      xhr.open('POST', autoMarkingUploadURL, true);
      // Add authorization header
      if (token) {
        xhr.setRequestHeader('Authorization', token);
      }
      xhr.send(formData);
    });
    
  } catch (error: any) {
    console.error('Direct upload error:', error);
    return {
      success: false,
      message: error.message || 'Upload failed'
    };
  }
};
const handleApiResponse = async (response: any): Promise<ApiResponse> => {
  console.log('Processing API Response:', {
    status: response.status,
    statusText: response.statusText,
    data: response.data
  });

  // Handle the actual API response format with collections
  if (response.data && response.data.success === true) {
    // Check if data is under 'collections' key
    const apiData = response.data.collections || response.data.data;
    
    // The API already provides questionDetails and summary in the correct format!
    if (apiData && apiData.questionDetails && apiData.summary) {
      const convertedData: AutoMarkingData = {
        module: apiData.module,
        moduleCode: apiData.moduleCode,
        pageNumber: apiData.pageNumber?.toString(),
        handbookPage: apiData.handbookPage?.toString(),
        summary: apiData.summary,
        questionDetails: apiData.questionDetails,
        teacherSummary: apiData.teacherSummary, // Add teacher summary
        questionsAndAnswers: apiData.questionsAndAnswers, // Add full Q&A data
        handbookAnswer: apiData.handbookAnswer // Add handbook answers
        ,
        subject_name: ''
      };
      
      return {
        success: true,
        data: convertedData
      };
    }
  }
  
  // Handle direct response (for XMLHttpRequest and fetch)
  if (response.success === true) {
    // Check if data is under 'collections' key
    const apiData = response.collections || response.data;
    
    if (apiData && apiData.questionDetails && apiData.summary) {
      const convertedData: AutoMarkingData = {
        module: apiData.module,
        moduleCode: apiData.moduleCode,
        pageNumber: apiData.pageNumber?.toString(),
        handbookPage: apiData.handbookPage?.toString(),
        summary: apiData.summary,
        questionDetails: apiData.questionDetails,
        teacherSummary: apiData.teacherSummary,
        questionsAndAnswers: apiData.questionsAndAnswers,
        handbookAnswer: apiData.handbookAnswer,
        subject_name: ''
      };
      
      return {
        success: true,
        data: convertedData
      };
    }
  }

  // Fallback for unexpected response format
  return {
    success: false,
    message: 'Unexpected response format from server'
  };
};

export const getMarkingHistory = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.get(autoMarkingHistoryURL);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('Get marking history error:', error);
    
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch marking history'
    };
  }
};

export const deleteMarkingResult = async (resultId: string): Promise<ApiResponse> => {
  try {
    const response = await axios.delete(`${autoMarkingResultURL}/${resultId}`);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('Delete marking result error:', error);
    
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to delete marking result'
    };
  }
};

export const exportMarkingResults = async (resultIds: string[], format: 'pdf' | 'excel' = 'pdf'): Promise<ApiResponse> => {
  try {
    const response = await axios.post(autoMarkingExportURL, {
      resultIds,
      format
    }, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `marking-results.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return {
      success: true,
      message: 'Results exported successfully'
    };
  } catch (error: any) {
    console.error('Export marking results error:', error);
    
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to export marking results'
    };
  }
};

export const retryMarking = async (file: File): Promise<ApiResponse> => {
  try {
    // Same as uploadForMarking but with retry flag
    const formData = new FormData();
    formData.append('image', file); // Use 'image' field name to match API

    const response = await axios.post(autoMarkingUploadURL, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000,
    });

    if (response.data) {
      if (response.data.success !== undefined) {
        return response.data;
      }
      
      if (response.data.summary || response.data.questionDetails) {
        return {
          success: true,
          data: response.data
        };
      }
      
      if (response.data.data) {
        return {
          success: true,
          data: response.data.data
        };
      }
    }

    return {
      success: false,
      message: 'Unexpected response format from server'
    };

  } catch (error: any) {
    console.error('Retry marking error:', error);
    
    if (error.response) {
      return {
        success: false,
        message: error.response.data?.message || `Server error: ${error.response.status}`
      };
    } else if (error.request) {
      return {
        success: false,
        message: 'No response from server. Please check your connection.'
      };
    } else {
      return {
        success: false,
        message: error.message || 'An unexpected error occurred'
      };
    }
  }
};

// Utility functions
export const calculateGrade = (percentage: number): string => {
  if (percentage >= 90) return 'A+';
  if (percentage >= 80) return 'A';
  if (percentage >= 70) return 'B';
  if (percentage >= 60) return 'C';
  if (percentage >= 50) return 'D';
  return 'F';
};

export const getPerformanceLevel = (percentage: number): string => {
  if (percentage >= 90) return 'Excellent';
  if (percentage >= 80) return 'Very Good';
  if (percentage >= 70) return 'Good';
  if (percentage >= 60) return 'Satisfactory';
  if (percentage >= 50) return 'Needs Improvement';
  return 'Requires Support';
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};